/**
 * Fix and Flip Calculator Component
 * Calculates loan amounts, ARV limits, and potential profit
 */

import React, { useState, useMemo } from "react";
import { Calculator, DollarSign, TrendingUp, Percent, Info } from "lucide-react";

const experienceLevels = [
  { label: "New (0-2 Flips)", purchaseLtv: 0.80, rehabLtv: 1.00, maxArv: 0.70 },
  { label: "Intermediate (3-5 Flips)", purchaseLtv: 0.90, rehabLtv: 1.00, maxArv: 0.725 },
  { label: "Experienced (5+ Flips)", purchaseLtv: 0.95, rehabLtv: 1.00, maxArv: 0.75 },
  { label: "Expert (10+ Flips)", purchaseLtv: 1.00, rehabLtv: 1.00, maxArv: 0.75 }
];

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

export default function FixFlipCalculator() {
  const [purchasePrice, setPurchasePrice] = useState(250000);
  const [rehabCosts, setRehabCosts] = useState(50000);
  const [afterRepairValue, setAfterRepairValue] = useState(400000);
  const [experienceLevel, setExperienceLevel] = useState(0);
  const [holdingMonths, setHoldingMonths] = useState(6);
  const [interestRate, setInterestRate] = useState(10.5);

  const calculations = useMemo(() => {
    const exp = experienceLevels[experienceLevel];
    
    // Calculate max loan based on purchase price
    const maxPurchaseLoan = purchasePrice * exp.purchaseLtv;
    
    // Calculate max rehab loan
    const maxRehabLoan = rehabCosts * exp.rehabLtv;
    
    // Calculate max loan based on ARV
    const maxArvLoan = afterRepairValue * exp.maxArv;
    
    // Total potential loan (capped by ARV)
    const totalPotentialLoan = maxPurchaseLoan + maxRehabLoan;
    const actualLoanAmount = Math.min(totalPotentialLoan, maxArvLoan);
    
    // Cash needed at closing
    const cashToClose = purchasePrice - Math.min(maxPurchaseLoan, actualLoanAmount - maxRehabLoan);
    
    // Interest costs (interest only on outstanding balance)
    const avgBalance = actualLoanAmount * 0.75; // Assuming average 75% drawn
    const monthlyInterest = (avgBalance * (interestRate / 100)) / 12;
    const totalInterest = monthlyInterest * holdingMonths;
    
    // Holding costs estimate (taxes, insurance, utilities)
    const monthlyHoldingCosts = afterRepairValue * 0.005; // ~0.5% of ARV per month
    const totalHoldingCosts = monthlyHoldingCosts * holdingMonths;
    
    // Selling costs (6% realtor, 2% closing)
    const sellingCosts = afterRepairValue * 0.08;
    
    // Total project cost
    const totalProjectCost = purchasePrice + rehabCosts + totalInterest + totalHoldingCosts + sellingCosts;
    
    // Potential profit
    const grossProfit = afterRepairValue - totalProjectCost;
    const roi = ((grossProfit / (cashToClose > 0 ? cashToClose : 1)) * 100);
    
    // LTV and ARV percentages
    const ltvPercent = (actualLoanAmount / purchasePrice) * 100;
    const arvPercent = (actualLoanAmount / afterRepairValue) * 100;
    
    return {
      maxPurchaseLoan,
      maxRehabLoan,
      maxArvLoan,
      actualLoanAmount,
      cashToClose: Math.max(0, cashToClose),
      totalInterest,
      totalHoldingCosts,
      sellingCosts,
      totalProjectCost,
      grossProfit,
      roi,
      ltvPercent,
      arvPercent,
      exp
    };
  }, [purchasePrice, rehabCosts, afterRepairValue, experienceLevel, holdingMonths, interestRate]);

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#e8821c] to-[#d67518] p-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
            <Calculator size={24} className="text-white" />
          </div>
          <div>
            <h3 
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-xl font-bold text-white"
            >
              Fix & Flip Calculator
            </h3>
            <p className="text-white/80 text-sm">Calculate your loan amount and potential profit</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Inputs */}
          <div className="space-y-5">
            <h4 className="font-bold text-[#0f2d52] mb-4">Property Details</h4>
            
            {/* Experience Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Experience Level
              </label>
              <select
                value={experienceLevel}
                onChange={(e) => setExperienceLevel(Number(e.target.value))}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#e8821c] focus:border-transparent"
              >
                {experienceLevels.map((level, idx) => (
                  <option key={idx} value={idx}>{level.label}</option>
                ))}
              </select>
            </div>

            {/* Purchase Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Purchase Price
              </label>
              <div className="relative">
                <DollarSign size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(Number(e.target.value))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#e8821c] focus:border-transparent"
                />
              </div>
            </div>

            {/* Rehab Costs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Rehab Costs
              </label>
              <div className="relative">
                <DollarSign size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={rehabCosts}
                  onChange={(e) => setRehabCosts(Number(e.target.value))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#e8821c] focus:border-transparent"
                />
              </div>
            </div>

            {/* After Repair Value */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                After Repair Value (ARV)
              </label>
              <div className="relative">
                <DollarSign size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={afterRepairValue}
                  onChange={(e) => setAfterRepairValue(Number(e.target.value))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#e8821c] focus:border-transparent"
                />
              </div>
            </div>

            {/* Holding Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Holding Period (Months)
              </label>
              <input
                type="range"
                min="3"
                max="18"
                value={holdingMonths}
                onChange={(e) => setHoldingMonths(Number(e.target.value))}
                className="w-full accent-[#e8821c]"
              />
              <div className="flex justify-between text-sm text-gray-500 mt-1">
                <span>3 mo</span>
                <span className="font-bold text-[#e8821c]">{holdingMonths} months</span>
                <span>18 mo</span>
              </div>
            </div>

            {/* Interest Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Interest Rate (%)
              </label>
              <div className="relative">
                <Percent size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  step="0.25"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#e8821c] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-4">
            <h4 className="font-bold text-[#0f2d52] mb-4">Loan & Profit Analysis</h4>
            
            {/* Loan Amount */}
            <div className="bg-[#e8821c]/10 rounded-xl p-5 border border-[#e8821c]/20">
              <div className="flex justify-between items-start mb-2">
                <span className="text-sm text-gray-600">Estimated Loan Amount</span>
                <div className="flex gap-2">
                  <span className="text-xs bg-[#e8821c]/20 text-[#e8821c] px-2 py-1 rounded">
                    {calculations.arvPercent.toFixed(1)}% of ARV
                  </span>
                </div>
              </div>
              <p className="text-3xl font-black text-[#e8821c]">
                {formatCurrency(calculations.actualLoanAmount)}
              </p>
              <div className="mt-2 text-xs text-gray-500">
                Purchase: {formatCurrency(Math.min(calculations.maxPurchaseLoan, calculations.actualLoanAmount - calculations.maxRehabLoan))} + 
                Rehab: {formatCurrency(Math.min(calculations.maxRehabLoan, calculations.actualLoanAmount))}
              </div>
            </div>

            {/* Cash to Close */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Cash Needed at Closing</span>
                <span className="text-xl font-bold text-[#0f2d52]">
                  {formatCurrency(calculations.cashToClose)}
                </span>
              </div>
            </div>

            {/* Cost Breakdown */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-2">
              <p className="text-sm font-medium text-gray-700 mb-3">Project Costs</p>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Interest Costs</span>
                <span className="text-gray-700">{formatCurrency(calculations.totalInterest)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Holding Costs (est.)</span>
                <span className="text-gray-700">{formatCurrency(calculations.totalHoldingCosts)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Selling Costs (8%)</span>
                <span className="text-gray-700">{formatCurrency(calculations.sellingCosts)}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                <span className="text-gray-700 font-medium">Total Project Cost</span>
                <span className="text-gray-900 font-bold">{formatCurrency(calculations.totalProjectCost)}</span>
              </div>
            </div>

            {/* Potential Profit */}
            <div className={`rounded-xl p-5 border ${calculations.grossProfit >= 0 ? 'bg-[#48d480]/10 border-[#48d480]/20' : 'bg-red-50 border-red-200'}`}>
              <div className="flex justify-between items-start mb-2">
                <span className="text-sm text-gray-600">Estimated Profit</span>
                {calculations.grossProfit >= 0 && (
                  <span className="text-xs bg-[#48d480]/20 text-[#48d480] px-2 py-1 rounded flex items-center gap-1">
                    <TrendingUp size={12} /> {calculations.roi.toFixed(0)}% ROI
                  </span>
                )}
              </div>
              <p className={`text-3xl font-black ${calculations.grossProfit >= 0 ? 'text-[#48d480]' : 'text-red-500'}`}>
                {formatCurrency(calculations.grossProfit)}
              </p>
            </div>

            {/* Disclaimer */}
            <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
              <Info size={14} className="flex-shrink-0 mt-0.5" />
              <span>
                This calculator provides estimates only. Actual loan terms, rates, and costs may vary. 
                Contact us for a personalized quote.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
